import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import encapsulatedStyles from './styles/outline-button.encapsulated.pcss?inline';
import globalStyles from './styles/outline-button.global.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { addIconElement } from '../../utilities/icon-utilities';

export const buttonVariants = [
  'light',
  'dark',
  'highlight',
  'square',
  'square-thin',
] as const;
export type ButtonVariant = (typeof buttonVariants)[number];

const componentName = 'outline-button';

/**
 * The OutlineButton component
 * @element outline-button
 * @slot - default slot for <button> or <a> markup
 */
@customElement('outline-button')
export class OutlineButton extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  /**
   * Class names for desired fontawesome icon
   */
  @property({ type: String, attribute: 'icon' })
  icon: string;

  /**
   * For rounded no text buttons
   */
  @property({ type: Boolean, attribute: 'icon-only' })
  iconOnly: boolean;

  /**
   * Buton variantions 'light', 'dark', 'highlight', 'square'.
   */
  @property({ type: String })
  variant: ButtonVariant;

  /**
   * If the button needs to be disabled.
   */
  @property({ type: Boolean })
  disabled: boolean;

  /**
   * Postiion of optional icon `before` or `after` button markup.
   */
  @property({ type: String, attribute: 'icon-position' })
  iconPosition: 'before' | 'after' = 'after';

  _mainElement: HTMLElement;

  connectedCallback(): void {
    super.connectedCallback();
    this.setMainElement();
    if (this._mainElement) {
      const elementTarget = this._mainElement.getAttribute('target');
      this._mainElement.classList.add('outline-btn');
      this._mainElement.classList.add(this.variant);

      // set .icon-only
      if (this.iconOnly) {
        this._mainElement.classList.add('icon-only');
      }

      // Handle disabled
      if (this.disabled) {
        const existingLabel = this._mainElement?.getAttribute('aria-label');
        this._mainElement.setAttribute(
          'aria-label',
          `${existingLabel} button disabled`
        );
        this._mainElement.classList.add('disabled');
      }

      // Create <i> tag and handle FA class names
      if (this.icon && this._mainElement) {
        addIconElement(this._mainElement, this.icon, this.iconPosition);
      }

      // Handles external link text for screen readers
      if (elementTarget && elementTarget === '_blank') {
        const existingAriaText =
          this._mainElement.getAttribute('aria-label') !== null
            ? this._mainElement.getAttribute('aria-label')
            : '';
        this._mainElement.setAttribute(
          'aria-label',
          `${existingAriaText} opens in new window`
        );
      }
    }
  }

  updated() {
    if (this.icon) {
      this.removeExistingIcon();
      addIconElement(this._mainElement, this.icon, this.iconPosition);
    }
  }

  render(): TemplateResult {
    return html` <div class="outline-button ${this.disabled ? 'disabled' : ''}">
      <slot></slot>
    </div>`;
  }

  setMainElement() {
    const element = this?.querySelector(
      'a, button, input[type="submit"], input[type="button"]'
    ) as HTMLElement;
    if (element) {
      this._mainElement = element;
    }
  }

  removeExistingIcon() {
    const existingIcon = this.querySelector('i');
    existingIcon?.remove();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineButton;
  }
}
